import { EncounterAssignmentErrorCodeEnum } from "@/network/useTakeEncounter";

/**
 * Short, clinician-facing text shown as a bullet in the override dialog for
 * each *overridable* block. Shared by every take/steal entry point so the
 * errorCode -> message lookup lives in exactly one place.
 */
export const OVERRIDE_CONFLICT_MESSAGES: Partial<
  Record<EncounterAssignmentErrorCodeEnum, string>
> = {
  [EncounterAssignmentErrorCodeEnum.NOT_LICENSED_FOR_STATE]:
    "You are not licensed to practice in this state",
  [EncounterAssignmentErrorCodeEnum.NOT_INSURANCE_ELIGIBLE]:
    "You are not covered by the patient's insurance",
};

export interface OverrideConflictContent {
  title: string;
  messages: string[];
}

interface AssignmentErrorDetailItem {
  errorCode?: EncounterAssignmentErrorCodeEnum;
}

interface AssignmentErrorLike {
  detail?:
    | {
        errors?: AssignmentErrorDetailItem[];
      }
    | string;
}

/**
 * Derive the override-dialog content from a take/steal error response.
 *
 * Returns null when nothing overridable is present (the caller should not open
 * the dialog). Reads every reported block from `detail.errors`.
 */
export function getOverrideConflictContent(
  error: unknown,
): OverrideConflictContent | null {
  const detail = (error as AssignmentErrorLike)?.detail;
  if (!detail || typeof detail === "string" || !Array.isArray(detail.errors)) {
    return null;
  }

  const messages = detail.errors.map(
    (item) => item.errorCode && OVERRIDE_CONFLICT_MESSAGES[item.errorCode],
  );

  // Override can only unblock the take when EVERY reported block is
  // overridable. If any block is non-overridable (e.g. pediatrics, partner
  // clearance), overriding is futile, so don't open the dialog -- the toast
  // surfaces the blocking reason instead. This also keeps the dialog
  // consistent with the toast (which reflects the first error): the dialog
  // now only appears when the first error is itself overridable.
  if (messages.length === 0 || messages.some((message) => !message)) {
    return null;
  }

  return {
    title: "Visit Conflict(s) Present",
    messages: messages.filter((message): message is string => Boolean(message)),
  };
}
